import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { professionalClientAuthService } from '../../services/professionalClientAuthService';
import { supabase } from '../../lib/supabase';

const ProfessionalClientLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.email || !formData?.password) {
      toast?.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      setLoading(true);
      const result = await professionalClientAuthService?.signIn(formData?.email, formData?.password);
      
      if (result?.success) {
        // Verify account_type in user metadata
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();
        
        if (userError) {
          toast?.error('Erreur de vérification du compte');
          await supabase?.auth?.signOut();
          return;
        }

        const accountType = user?.user_metadata?.account_type;
        
        if (accountType !== 'professional') {
          toast?.error('Ce compte n\'est pas un compte professionnel');
          await supabase?.auth?.signOut();
          return;
        }

        // Verify professional_clients record exists
        if (!result?.professionalClient) {
          toast?.error('Profil professionnel introuvable. Veuillez contacter le support.');
          await supabase?.auth?.signOut();
          return;
        }

        toast?.success('Connexion réussie !');
        
        // Small delay to ensure auth state is fully propagated
        setTimeout(() => {
          navigate('/professional-dashboard/vue-ensemble', { replace: true });
        }, 100);
      } else {
        toast?.error(result?.error || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast?.error(error?.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <PublicNavigation />

      <div className="flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Icon name="Briefcase" size={32} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Espace Professionnel</h1>
              <p className="text-gray-600 mt-2">Connectez-vous à votre compte pro</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email professionnel
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="votre@entreprise.com"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
              </div>

              <div className="flex items-center justify-between">
                <Link
                  to="/professional-client/forgot-password"
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Connexion...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="LogIn" size={20} />
                    <span>Se connecter</span>
                  </div>
                )}
              </Button>
            </form>

            {/* Signup Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Pas encore de compte pro ?{' '}
                <Link
                  to="/professional-client-register"
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Créer un compte
                </Link>
              </p>
            </div>

            {/* Info Box */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium mb-1">Avantages de l'espace pro</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Historique complet de vos devis</li>
                    <li>• Suivi des emails et rappels automatiques</li>
                    <li>• Gestion de vos réservations d'expéditions</li>
                    <li>• Analyse détaillée des coûts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalClientLogin;